import axios from "axios";

const portalBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL
  || `${window.location.protocol}//${window.location.host}`;
const isLocalDevelopment = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const backendEntryUrl = process.env.REACT_APP_PORTFOLIO_API_BASE_URL
  || (isLocalDevelopment
    ? "/backend/public/index.php"
    : `${portalBaseUrl.replace(/\/$/, "")}/backend/public/index.php`);

const portfolioClient = axios.create({
  baseURL: backendEntryUrl,
  headers: { "Content-Type": "application/json" }
});

const request = async config => {
  const response = await portfolioClient({
    ...config,
    url: "",
    params: {
      ...(config.params || {}),
      route: `/api/portfolio${config.url}`
    }
  });
  return response.data;
};

const fileContent = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = () => reject(new Error(`File read failed: ${file.name}`));
  reader.readAsDataURL(file);
});

export const PortfolioListInvestorCapitalPositions = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/capital-positions`
});

export const PortfolioListInvestorPositions = async investorId => request({
  method: "get",
  url: `/investors/${investorId}/capital-positions`
});

export const PortfolioDeleteInvestorCapitalPosition = async (positionId, deletedBy) => request({
  method: "delete",
  url: `/capital-positions/${positionId}`,
  data: { deleted_by: deletedBy, confirmation: "DELETE" }
});

export const PortfolioListFunds = async () => request({
  method: "get",
  url: "/funds"
});

export const PortfolioListCurrencies = async () => request({
  method: "get",
  url: "/currencies"
});

export const PortfolioUpdateFund = async (fundId, fund) => request({ method: "put", url: `/funds/${fundId}`, data: fund });

export const PortfolioCreateFund = async fund => request({
  method: "post",
  url: "/funds",
  data: fund
});

export const PortfolioListFundUnitClasses = async (fundId, subscribableOnly = false) => request({
  method: "get", url: `/funds/${fundId}/unit-classes`, params: subscribableOnly ? { subscribable_only: 1 } : {}
});

export const PortfolioCreateFundUnitClass = async (fundId, unitClass) => request({ method: "post", url: `/funds/${fundId}/unit-classes`, data: unitClass });
export const PortfolioUpdateFundUnitClass = async (fundId, classId, unitClass) => request({ method: "put", url: `/funds/${fundId}/unit-classes/${classId}`, data: unitClass });

export const PortfolioUpdateFundNav = async (fundId, nav) => request({
  method: "put",
  url: `/funds/${fundId}/nav`,
  data: nav
});

export const PortfolioListFundNavHistory = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/nav-history`
});

export const PortfolioListPublicFundDocuments = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/public-documents`
});

export const PortfolioDownloadDocument = async documentId => request({
  method: "get",
  url: `/documents/${documentId}/download`
});

export const PortfolioUploadFundNavDocuments = async (fundId, navHistoryId, files, effectiveDate, uploadedBy) => request({
  method: "post",
  url: `/funds/${fundId}/nav-documents`,
  data: {
    effective_date: effectiveDate,
    nav_history_id: navHistoryId,
    uploaded_by: uploadedBy,
    documents: await Promise.all(files.map(async file => ({
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      content: await fileContent(file)
    })))
  }
});

const wireDocuments = async files => Promise.all(files.map(async file => ({
  file_name: file.name,
  content_type: file.type || "application/octet-stream",
  content: await fileContent(file)
})));

export const PortfolioUploadApplicationWireDocuments = async (applicationId, files, uploadedBy) => request({
  method: "post",
  url: `/applications/${applicationId}/wire-documents`,
  data: { uploaded_by: uploadedBy, documents: await wireDocuments(files) }
});

export const PortfolioUploadCalledWireDocuments = async (capitalPositionId, adjustmentId, files, uploadedBy) => request({
  method: "post",
  url: `/capital-positions/${capitalPositionId}/called-adjustments/${adjustmentId}/wire-documents`,
  data: { uploaded_by: uploadedBy, documents: await wireDocuments(files) }
});

export const PortfolioListInvestorDocuments = async investorId => request({
  method: "get",
  url: `/investors/${investorId}/documents`
});

export const PortfolioUploadInvestorDocuments = async (investorId, files, options) => request({
  method: "post",
  url: `/investors/${investorId}/documents`,
  data: {
    uploaded_by: options.uploadedBy,
    investor_name: options.investorName,
    fund_id: options.fundId || null,
    document_type: options.documentType,
    access_role: options.accessRole,
    sub_folder: options.subFolder || "",
    documents: await Promise.all(files.map(async file => ({
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      content: await fileContent(file)
    })))
  }
});

export const PortfolioApplyFund = async (appliedBy, fundId, investorId, application) => request({
  method: "post",
  url: "/applications",
  data: {
    applied_by: appliedBy,
    fund_id: fundId,
    investor_id: investorId,
    ...application
  }
});

export const PortfolioListApplications = async () => request({
  method: "get",
  url: "/applications"
});

export const PortfolioLookupJointOwner = async email => request({
  method: "get",
  url: "/users/lookup",
  params: { email }
});

export const PortfolioListOnboardedUsers = async scopeUserId => request({
  method: "get",
  url: "/users",
  params: { scope_user_id: scopeUserId }
});

export const PortfolioListDelegateRelationships = async scopeUserId => request({
  method: "get",
  url: "/delegate-relationships",
  params: { scope_user_id: scopeUserId }
});

export const PortfolioListFundUnitTransfers = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/unit-transfers`
});

export const PortfolioListEligibleTransferSellers = async (fundId, effectiveDate, unitClassId = "") => request({
  method: "get",
  url: `/funds/${fundId}/unit-transfers/eligible-sellers`,
  params: {
    effective_date: effectiveDate,
    fund_unit_class_id: unitClassId || undefined
  }
});

export const PortfolioPreviewFundUnitTransfer = async (fundId, transfer) => request({
  method: "post",
  url: `/funds/${fundId}/unit-transfers/preview`,
  data: transfer
});

export const PortfolioCreateFundUnitTransfer = async (fundId, transfer) => request({
  method: "post",
  url: `/funds/${fundId}/unit-transfers`,
  data: transfer
});

export const PortfolioGetFundUnitTransfer = async transferId => request({
  method: "get",
  url: `/unit-transfers/${transferId}`
});

export const PortfolioReverseFundUnitTransfer = async (transferId, reversal) => request({
  method: "post",
  url: `/unit-transfers/${transferId}/reverse`,
  data: reversal
});

export const PortfolioCreateInvestmentOwnership = async ownership => request({
  method: "post",
  url: "/ownerships",
  data: ownership
});

export const PortfolioGetInvestmentOwnership = async ownershipId => request({
  method: "get",
  url: `/ownerships/${ownershipId}`
});

export const PortfolioUpdateInvestmentOwnership = async (ownershipId, ownership) => request({
  method: "patch",
  url: `/ownerships/${ownershipId}`,
  data: ownership
});

export const PortfolioAddJointOwner = async (ownershipId, owner) => request({
  method: "post",
  url: `/ownerships/${ownershipId}/owners`,
  data: owner
});

export const PortfolioUpdateJointOwner = async (ownershipId, ownerId, owner) => request({
  method: "patch",
  url: `/ownerships/${ownershipId}/owners/${ownerId}`,
  data: owner
});

export const PortfolioRemoveJointOwner = async (ownershipId, ownerId, performedBy, reason = "") => request({
  method: "delete",
  url: `/ownerships/${ownershipId}/owners/${ownerId}`,
  data: { performed_by: performedBy, reason }
});

export const PortfolioRecordJointOwnerConsent = async (ownershipId, consent) => request({
  method: "post",
  url: `/ownerships/${ownershipId}/consent`,
  data: consent
});

export const PortfolioReviewJointOwner = async (ownershipId, ownerId, decision) => request({
  method: "post",
  url: `/ownerships/${ownershipId}/owners/${ownerId}/admin-decision`,
  data: decision
});

export const PortfolioListJointInvestments = async investorId => request({
  method: "get",
  url: `/investors/${investorId}/joint-investments`
});

export const PortfolioCreateAnnouncement = async announcement => request({
  method: "post",
  url: "/announcements",
  data: announcement
});

export const PortfolioUpdateAnnouncement = async (announcementId, announcement) => request({
  method: "put",
  url: `/announcements/${announcementId}`,
  data: announcement
});

export const PortfolioListInvestorAnnouncements = async investorId => request({
  method: "get",
  url: `/investors/${investorId}/announcements`
});

export const PortfolioListFundFeeSchedules = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/fee-schedules`
});

export const PortfolioCreateFundFeeSchedule = async (fundId, schedule) => request({
  method: "post",
  url: `/funds/${fundId}/fee-schedules`,
  data: schedule
});

export const PortfolioUpdateFundFeeSchedule = async (scheduleId, schedule) => request({
  method: "put",
  url: `/fee-schedules/${scheduleId}`,
  data: schedule
});

export const PortfolioDeleteFundFeeSchedule = async scheduleId => request({
  method: "delete",
  url: `/fee-schedules/${scheduleId}`
});

export const PortfolioReviewApplication = async (
  applicationId,
  reviewedBy,
  newStatus,
  notes,
  approvalTerms = {}
) => request({
  method: "put",
  url: `/applications/${applicationId}/review`,
  data: {
    reviewed_by: reviewedBy,
    new_status: newStatus,
    notes,
    ...approvalTerms
  }
});

export const PortfolioUpdateInvestorCalledAmount = async (
  capitalPositionId,
  additionalAmount,
  requestId,
  reason,
  createdBy
) => request({
  method: "put",
  url: `/capital-positions/${capitalPositionId}/called`,
  data: {
    additional_amount: additionalAmount,
    request_id: requestId,
    reason,
    created_by: createdBy
  }
});

export const PortfolioListInvestorCalledHistory = async capitalPositionId => request({
  method: "get",
  url: `/capital-positions/${capitalPositionId}/called-history`
});

export const PortfolioAttachCalledDocuments = async (
  capitalPositionId,
  adjustmentId,
  uploadedBy,
  documents
) => request({
  method: "post",
  url: `/capital-positions/${capitalPositionId}/called-adjustments/${adjustmentId}/documents`,
  data: { uploaded_by: uploadedBy, documents }
});

export const PortfolioGetInvestorPaymentSchedule = async capitalPositionId => request({
  method: "get",
  url: `/capital-positions/${capitalPositionId}/payment-schedule`
});

export const PortfolioListFundDistributions = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/distributions`
});

export const PortfolioPreviewFundDistribution = async (fundId, distribution) => request({
  method: "post",
  url: `/funds/${fundId}/fund-distributions/preview`,
  data: distribution
});

export const PortfolioPreviewFundDistributionEmail = async (fundId, distribution) => request({
  method: "post",
  url: `/funds/${fundId}/fund-distributions/email-preview`,
  data: distribution
});

export const PortfolioCreateFundDistribution = async (fundId, distribution) => request({
  method: "post",
  url: `/funds/${fundId}/fund-distributions`,
  data: distribution
});

export const PortfolioUploadFundDistributionDocuments = async (distributionId, files, uploadedBy) => request({
  method: "post",
  url: `/fund-distributions/${distributionId}/documents`,
  data: {
    uploaded_by: uploadedBy,
    documents: await Promise.all(files.map(async file => ({
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      content: await fileContent(file)
    })))
  }
});

export const PortfolioListFundLevelDistributions = async fundId => request({
  method: "get",
  url: `/funds/${fundId}/fund-distributions`
});

export const PortfolioListFundDistributionAllocations = async distributionId => request({
  method: "get",
  url: `/fund-distributions/${distributionId}/allocations`
});

export const PortfolioPreviewFinalizedDistributionEmail = async (distributionId, investorId) => request({
  method: "post",
  url: `/fund-distributions/${distributionId}/email-preview`,
  data: { investor_id: investorId }
});

export const PortfolioResendFinalizedDistributionEmail = async (distributionId, resend) => request({
  method: "post",
  url: `/fund-distributions/${distributionId}/resend-email`,
  data: resend
});

export const PortfolioReverseFundDistribution = async (distributionId, reversal) => request({
  method: "post",
  url: `/fund-distributions/${distributionId}/reverse`,
  data: reversal
});

export const PortfolioCorrectFundDistribution = async (distributionId, correction) => request({
  method: "post",
  url: `/fund-distributions/${distributionId}/correct`,
  data: correction
});

export const PortfolioListInvestorDistributions = async capitalPositionId => request({
  method: "get",
  url: `/capital-positions/${capitalPositionId}/distributions`
});

export const PortfolioListInvestorDistributionElections = async investorId => request({
  method: "get",
  url: `/investors/${investorId}/distribution-elections`
});

export const PortfolioListDistributionElections = async () => request({
  method: "get",
  url: "/distribution-elections"
});

export const PortfolioSubmitDistributionElection = async (investorId, distributionId, election) => request({
  method: "put",
  url: `/investors/${investorId}/distributions/${distributionId}/election`,
  data: election
});

export const PortfolioReviewDistributionElection = async (electionId, review) => request({
  method: "post",
  url: `/distribution-elections/${electionId}/review`,
  data: review
});

export const PortfolioAddInvestorDistribution = async (
  capitalPositionId,
  amount,
  distributionDate,
  description,
  requestId,
  createdBy
) => request({
  method: "post",
  url: `/capital-positions/${capitalPositionId}/distributions`,
  data: {
    amount,
    distribution_date: distributionDate,
    description,
    request_id: requestId,
    created_by: createdBy
  }
});

export const PortfolioListPaymentDestinations = async userId => request({
  method: "get",
  url: `/investors/${userId}/payment-destinations`
});

export const PortfolioCreatePaymentDestination = async (userId, destination) => request({
  method: "post",
  url: `/investors/${userId}/payment-destinations`,
  data: destination
});

export const PortfolioUpdatePaymentDestination = async (destinationId, destination) => request({
  method: "put",
  url: `/payment-destinations/${destinationId}`,
  data: destination
});

export const PortfolioDeletePaymentDestination = async (destinationId, actorId, reason = "") => request({
  method: "delete",
  url: `/payment-destinations/${destinationId}`,
  data: { actor_id: actorId, reason }
});

export const PortfolioRevealPaymentDestination = async (destinationId, actorId, reason = "Administrative review") => request({
  method: "post",
  url: `/payment-destinations/${destinationId}/reveal`,
  data: { actor_id: actorId, reason }
});

export const PortfolioListEmailTemplates = async () => request({
  method: "get",
  url: "/email-templates"
});

export const PortfolioGetEmailTemplateMetadata = async () => request({
  method: "get",
  url: "/email-templates/metadata"
});

export const PortfolioCreateEmailTemplate = async template => request({
  method: "post",
  url: "/email-templates",
  data: template
});

export const PortfolioSendTestEmailTemplate = async template => request({
  method: "post",
  url: "/email-templates/send-test",
  data: template
});

export const PortfolioUpdateEmailTemplate = async (templateId, template) => request({
  method: "put",
  url: `/email-templates/${templateId}`,
  data: template
});

export const PortfolioPublishEmailTemplate = async (templateId, actorId) => request({
  method: "post",
  url: `/email-templates/${templateId}/publish`,
  data: { actor_id: actorId }
});

export const PortfolioDuplicateEmailTemplate = async (templateId, actorId) => request({
  method: "post",
  url: `/email-templates/${templateId}/duplicate`,
  data: { actor_id: actorId }
});

export const PortfolioArchiveEmailTemplate = async (templateId, actorId) => request({
  method: "post",
  url: `/email-templates/${templateId}/archive`,
  data: { actor_id: actorId }
});

export const PortfolioListEmailCommunications = async params => request({
  method: "get",
  url: "/email-communications",
  params
});

export const PortfolioGetEmailCommunication = async communicationId => request({
  method: "get",
  url: `/email-communications/${communicationId}`
});

export const PortfolioGetEmailCommunicationRecipient = async (communicationId, recipientId) => request({
  method: "get",
  url: `/email-communications/${communicationId}/recipients/${recipientId}`
});
